import styled from 'styled-components';
import { FlexRowMiddle } from './Flex';
import { IconButton } from './Icon';
import { Text } from './Text';
import { KeyboardEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import IconEdit from '../../../public/icons/edit.svg?react';
import IconClose from '../../../public/icons/close.svg?react';
import { sleep } from '../../scripts/scripts';

const StyledEditCell = styled(FlexRowMiddle)`
	max-width: 75%;
	min-width: 150px;
	width: 100%;
	height: 100%;
	overflow: hidden;
`;

const StyledInput = styled.input`
	min-width: 0;
	flex: 1;
	height: 100%;
	background: ${({ theme }) => theme.input_bg};
	border: none;
	border-bottom: 2px solid ${({ theme }) => theme.text_theme};
	outline: none;
	padding: 0 0.5rem;
	color: ${({ theme }) => theme.text_desc_main};

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`;

export function EditCell({
	children,
	value,
	saveValue,
	onInputMap,
	onBlurMap,
	disabled,
}: {
	disabled?: boolean;
	children: ReactNode;
	value: string;
	saveValue: (value: string) => Promise<void>;
	onInputMap: (value: string) => string | undefined;
	onBlurMap: (value: string) => string | undefined;
}) {
	const [editInput, setEditInput] = useState<string>('');

	const [saving, setSaving] = useState<boolean>(false);

	const [editing, setEditing] = useState<boolean>(false);
	const editingRef = useRef<boolean>(false);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const onClickEdit = () => {
		setEditing(true);
		editingRef.current = true;
		setTimeout(() => inputRef?.current?.focus(), 20);
		setEditInput(value);
	};

	const onClickCancel = (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		setEditing(false);
		editingRef.current = false;
	};

	const onBlur = async () => {
		await sleep(100);
		if (!editingRef.current) return;
		if (onBlurMap && onBlurMap?.(editInput) === undefined) return;
		setSaving(true);
		await saveValue(editInput);
		setEditing(false);
		setSaving(false);
	};

	const onInput = (input: string) => {
		if (onInputMap && onInputMap(input) === undefined) return;
		setEditInput(input);
	};

	const onKeyUp = (e: KeyboardEvent) => {
		if (e.key === 'Enter') {
			onBlur();
		}
	};

	return (
		<StyledEditCell>
			{!editing && (
				<>
					<Text $color="text_desc_main" $size="1.3rem" $weight="400">
						{children}
					</Text>
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={() => onClickEdit()}
						disabled={disabled}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
					>
						<IconEdit />
					</IconButton>
				</>
			)}
			{editing && (
				<>
					<StyledInput
						ref={inputRef}
						value={editInput}
						onBlur={() => onBlur()}
						onChange={(v) => onInput(v.target.value)}
						disabled={saving}
						onKeyUp={onKeyUp}
					/>
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={onClickCancel}
						disabled={saving || disabled}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
					>
						<IconClose />
					</IconButton>
				</>
			)}
		</StyledEditCell>
	);
}
